import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useQuery, useQueryClient, type UseQueryOptions } from "@tanstack/react-query";
import { useApi, useRealtimeEvent } from "@ncp/api-client";
// The hooks' changes go through the shared save helper (S 2.8) – the answer is checked before anything reads as done – and stay silent: the
// screens that call them report the outcome.
// The save helper alone (not the whole design system): this module is also bundled into pages through @ncp/page-kit.
import { useSave } from "@ncp/ui/lib/save";
import { AssistantApi, type KnowledgeUploadMeta } from "./client";
import { turnSourcesOf } from "./types";
import { applyStreamEvent } from "./streamReducer";
import type {
  AnswerBlock,
  Artifact,
  ArtifactExportFormat,
  ArtifactFilter,
  AssistantCatalog,
  AssistantStreamEvent,
  AskUserEvent,
  AttachArtifactRequest,
  ClientToolCall,
  CreateThreadRequest,
  DeepLinks,
  DeepLinkTarget,
  EditArtifactRequest,
  Home,
  InboxFilter,
  InboxPage,
  InboxSummary,
  KnowledgeCollection,
  KnowledgeSourceKey,
  TurnSourceChoice,
  KnowledgeDocument,
  KnowledgeDocumentDetail,
  OpenPage,
  PatchThreadRequest,
  SendMessageRequest,
  SurfaceFocus,
  SurfaceSeen,
  SurfaceState,
  Thread,
  ThreadDetail,
  ThreadFilter,
  UiEvent,
  UploadedAttachment,
} from "./types";

// ---------------------------------------------------------------- query keys
export const assistantKeys = {
  all: ["assistant"] as const,
  catalog: () => ["assistant", "catalog"] as const,
  home: (workspace?: string | null) => ["assistant", "home", workspace ?? "*"] as const,
  threads: (filter: ThreadFilter = {}) => ["assistant", "threads", filter] as const,
  thread: (id: string) => ["assistant", "thread", id] as const,
  links: (target: DeepLinkTarget) => ["assistant", "links", target] as const,
  artifacts: (filter: ArtifactFilter = {}) => ["assistant", "artifacts", filter] as const,
  artifact: (id: string) => ["assistant", "artifact", id] as const,
  artifactVersions: (id: string) => ["assistant", "artifact", id, "versions"] as const,
  inbox: (filter: InboxFilter = {}) => ["assistant", "inbox", filter] as const,
  inboxSummary: (workspace?: string | null) => (workspace === undefined ? ["assistant", "inbox", "summary"] as const : ["assistant", "inbox", "summary", workspace ?? "*"] as const),
  collections: (workspace?: string | null) => ["assistant", "knowledge", "collections", workspace ?? "*"] as const,
  documents: (collection: string, opts: object = {}) => ["assistant", "knowledge", "documents", collection, opts] as const,
  document: (id: string) => ["assistant", "knowledge", "document", id] as const,
  tools: () => ["assistant", "tools"] as const,
  apps: (key: string, locale: string) => ["assistant", "apps", key, locale] as const,
  app: (key: string, page: string, locale: string) => ["assistant", "app", key, page, locale] as const,
};

/** The Surface API client bound to the ambient `ApiProvider`. */
export function useAssistantApi(): AssistantApi {
  const api = useApi();
  return useMemo(() => new AssistantApi(api), [api]);
}

type Opts<T> = Omit<UseQueryOptions<T, Error, T, readonly unknown[]>, "queryKey" | "queryFn">;

// ---------------------------------------------------------------- shell / home
export function useCatalog(options?: Opts<AssistantCatalog>) {
  const sdk = useAssistantApi();
  return useQuery({ queryKey: assistantKeys.catalog(), queryFn: () => sdk.catalog(), staleTime: 60_000, ...options });
}

export function useHome(workspaceKey?: string | null, options?: Opts<Home>) {
  const sdk = useAssistantApi();
  return useQuery({ queryKey: assistantKeys.home(workspaceKey), queryFn: () => sdk.home(workspaceKey), staleTime: 20_000, ...options });
}

// ---------------------------------------------------------------- threads
export function useThreads(filter: ThreadFilter = {}, options?: Opts<Thread[]>) {
  const sdk = useAssistantApi();
  return useQuery({ queryKey: assistantKeys.threads(filter), queryFn: () => sdk.threads(filter), ...options });
}

export function useThread(id: string | undefined, options?: Opts<ThreadDetail>) {
  const sdk = useAssistantApi();
  return useQuery({ queryKey: assistantKeys.thread(id ?? ""), queryFn: () => sdk.thread(id!), enabled: !!id, ...options });
}

export function useCreateThread() {
  const sdk = useAssistantApi();
  const qc = useQueryClient();
  return useSave({
    report: "silent",
    mutationFn: (input: CreateThreadRequest) => sdk.createThread(input),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["assistant", "threads"] });
      void qc.invalidateQueries({ queryKey: ["assistant", "home"] });
    },
  });
}

/** A host surface's conversation about one case: resolves (or creates) it; `fresh` starts a new one. */
export function useCaseThread(surfaceKey: string) {
  const sdk = useAssistantApi();
  const qc = useQueryClient();
  return useSave({
    report: "silent",
    mutationFn: ({ caseId, locale, fresh }: { caseId: string; locale?: string; fresh?: boolean }) => sdk.caseThread(caseId, surfaceKey, { locale, fresh }),
    onSuccess: (t) => {
      qc.setQueryData(["assistant", "case-thread", surfaceKey, t.caseId], t);
      void qc.invalidateQueries({ queryKey: ["assistant", "threads"] });
    },
  });
}

// ---------------------------------------------------------------- registry deep links
/** Links of the solution registry for one target (`enabled` only when the target's id is known). */
export function useDeepLinks(target: DeepLinkTarget | null, options?: Opts<DeepLinks>) {
  const sdk = useAssistantApi();
  return useQuery({ queryKey: assistantKeys.links(target ?? { scope: "home" }), queryFn: () => sdk.links(target!), enabled: !!target, staleTime: 5 * 60_000, ...options });
}

/** Resolves registry links on demand (click handlers that only have an id). */
export function useResolveDeepLinks() {
  const sdk = useAssistantApi();
  const qc = useQueryClient();
  return useCallback((target: DeepLinkTarget) => qc.fetchQuery({ queryKey: assistantKeys.links(target), queryFn: () => sdk.links(target), staleTime: 5 * 60_000 }), [qc, sdk]);
}

export function usePatchThread() {
  const sdk = useAssistantApi();
  const qc = useQueryClient();
  return useSave({
    report: "silent",
    mutationFn: ({ id, patch }: { id: string; patch: PatchThreadRequest }) => sdk.patchThread(id, patch),
    onSuccess: (thread) => {
      qc.setQueryData<ThreadDetail>(assistantKeys.thread(thread.id), (d) => (d ? { ...d, thread } : d));
      void qc.invalidateQueries({ queryKey: ["assistant", "threads"] });
      void qc.invalidateQueries({ queryKey: ["assistant", "home"] });
    },
  });
}

export function useDeleteThread() {
  const sdk = useAssistantApi();
  const qc = useQueryClient();
  return useSave({
    report: "silent",
    mutationFn: (id: string) => sdk.deleteThread(id),
    onSuccess: (_, id) => {
      qc.removeQueries({ queryKey: assistantKeys.thread(id) });
      void qc.invalidateQueries({ queryKey: ["assistant", "threads"] });
      void qc.invalidateQueries({ queryKey: ["assistant", "home"] });
    },
  });
}

export function useMessageFeedback(threadId: string) {
  const sdk = useAssistantApi();
  const qc = useQueryClient();
  return useSave({
    report: "silent",
    mutationFn: ({ messageId, feedback, note }: { messageId: string; feedback: number | null; note?: string | null }) => sdk.feedback(threadId, messageId, feedback, note),
    onSuccess: (message) => {
      qc.setQueryData<ThreadDetail>(assistantKeys.thread(threadId), (d) => (d ? { ...d, messages: d.messages.map((m) => (m.id === message.id ? message : m)) } : d));
    },
  });
}

export function useSaveMessage(threadId: string) {
  const sdk = useAssistantApi();
  const qc = useQueryClient();
  return useSave({
    report: "silent",
    mutationFn: (messageId: string) => sdk.saveMessage(threadId, messageId),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["assistant", "artifacts"] });
      void qc.invalidateQueries({ queryKey: assistantKeys.thread(threadId) });
    },
  });
}

/** Confirm / cancel a staged action. The outcome line is appended to the thread and what the action touched is refetched. */
export function useDecideConfirmation(threadId: string | undefined) {
  const sdk = useAssistantApi();
  const qc = useQueryClient();
  return useSave({
    report: "silent",
    mutationFn: ({ confirmationId, accept, edits }: { confirmationId: string; accept: boolean; edits?: Record<string, unknown> }) => sdk.decideConfirmation(threadId!, confirmationId, accept, edits),
    onSuccess: (r) => {
      if (!threadId) return;
      qc.setQueryData<ThreadDetail>(assistantKeys.thread(threadId), (d) => d ? {
        ...d,
        messages: d.messages.some((m) => m.id === r.message.id) ? d.messages : [...d.messages, r.message],
        pendingConfirmations: d.pendingConfirmations.filter((c) => c.id !== r.confirmation.id),
        confirmations: [...(d.confirmations ?? []).filter((c) => c.id !== r.confirmation.id), r.confirmation],
      } : d);
      void qc.invalidateQueries({ queryKey: assistantKeys.thread(threadId) });
      void qc.invalidateQueries({ queryKey: ["assistant", "threads"] });
      void qc.invalidateQueries({ queryKey: ["assistant", "home"] });
      if (r.confirmation.result?.caseId) {
        void qc.invalidateQueries({ queryKey: ["matters"] });
        void qc.invalidateQueries({ queryKey: ["assistant", "artifacts"] });
      }
    },
  });
}

/**
 * Keeps a thread fresh from the background: delegated agents post their drafts as `thread.message` and stream `thread.step`
 * to the owner. Both refetch the thread (and the run list / home) so the conversation updates without a reload.
 */
export function useThreadRealtime(threadId: string | undefined, onStep?: (payload: ThreadStepPayload) => void): void {
  const qc = useQueryClient();
  const ref = useRef(onStep);
  ref.current = onStep;
  useRealtimeEvent<ThreadMessagePayload>(
    "thread.message",
    (p) => {
      if (threadId && p.threadId !== threadId) return;
      void qc.invalidateQueries({ queryKey: assistantKeys.thread(p.threadId) });
      void qc.invalidateQueries({ queryKey: ["assistant", "threads"] });
      void qc.invalidateQueries({ queryKey: ["assistant", "home"] });
      void qc.invalidateQueries({ queryKey: ["assistant", "inbox"] });
      if (p.artifactIds?.length) void qc.invalidateQueries({ queryKey: ["assistant", "artifacts"] });
    },
    true,
  );
  // The review after a turn (memory kept, a Knowledge Bank offer) lands on the answer a moment after it: the thread and memory refresh.
  useRealtimeEvent<{ threadId: string }>(
    "thread.review",
    (p) => {
      if (threadId && p.threadId !== threadId) return;
      void qc.invalidateQueries({ queryKey: assistantKeys.thread(p.threadId) });
      void qc.invalidateQueries({ queryKey: ["econic", "memory"] });
    },
    true,
  );
  useRealtimeEvent<ThreadArtifactsPayload>(
    "thread.artifacts",
    (p) => {
      if (threadId && p.threadId !== threadId) return;
      void qc.invalidateQueries({ queryKey: ["assistant", "artifacts"] });
      void qc.invalidateQueries({ queryKey: assistantKeys.thread(p.threadId) });
    },
    true,
  );
  useRealtimeEvent<ThreadStepPayload>(
    "thread.step",
    (p) => {
      if (threadId && p.threadId !== threadId) return;
      ref.current?.(p);
      if (p.step?.kind === "Completed" || p.step?.kind === "Failed") {
        void qc.invalidateQueries({ queryKey: assistantKeys.thread(p.threadId) });
        void qc.invalidateQueries({ queryKey: ["assistant", "home"] });
      }
    },
    true,
  );
}

export interface ThreadMessagePayload {
  threadId: string;
  messageId: string;
  agentKey?: string | null;
  runId?: string | null;
  proposalId?: string | null;
  /** Tables and charts the agent saved with its draft. */
  artifactIds?: string[] | null;
}

/** Artifacts an agent saved to a thread without posting a message (a matter's thread, or an inline delegation). */
export interface ThreadArtifactsPayload {
  threadId: string;
  runId?: string | null;
  artifactIds: string[];
}

export interface ThreadStepPayload {
  runId: string;
  caseId?: string | null;
  threadId: string;
  agentKey?: string;
  kind?: "Token";
  text?: string;
  step?: { id: string; sequence: number; kind: string; title: string; detail?: string | null; occurredAt: string; durationMs?: number | null };
}

// ---------------------------------------------------------------- streaming turn
export interface LiveToolCall {
  id: string;
  tool: string;
  agentKey?: string | null;
  phase: string;
  summary?: string | null;
  durationMs?: number | null;
  startedAt: number;
}

export interface LiveTurn {
  /** The user's text as sent (for optimistic rendering before the server echoes it). */
  userText: string;
  attachments: UploadedAttachment[];
  /** Streamed assistant content so far. */
  content: string;
  stage?: string | null;
  stageDetail?: string | null;
  toolCalls: LiveToolCall[];
  citations: Extract<AssistantStreamEvent, { type: "citation" }>[];
  proposals: Extract<AssistantStreamEvent, { type: "proposal" }>[];
  artifacts: Extract<AssistantStreamEvent, { type: "artifact" }>[];
  /** Documents being written in the person's pane (`artifact_delta` frames), by artifact id: the body so far, and whether it is saved. */
  writing?: Record<string, { text: string; done: boolean }>;
  confirmations: Extract<AssistantStreamEvent, { type: "confirm" }>[];
  delegations: Extract<AssistantStreamEvent, { type: "delegation" }>[];
  /** UI blocks rendered so far, in order; a block re-rendered under the same id replaces the earlier one. */
  blocks: AnswerBlock[];
  /** Changes the builder tools made to the screen (`ui` frames); the host applies them when the turn settles. */
  ui: UiEvent[];
  /** The agent's questions (`ask_user`); the turn ends with them. */
  ask?: AskUserEvent | null;
  /** What the model was given about the screen (the `status: surface` frame). */
  surfaceSeen?: SurfaceSeen | null;
  /** The screen the message was sent from, as sent (for the "now on …" divider before the server echoes it). */
  surface?: Pick<SurfaceState, "kind" | "surface" | "route" | "title" | "titleAr" | "department"> | null;
  /** A scope line under the user's words ("For my department"). */
  scope?: string | null;
  final?: Extract<AssistantStreamEvent, { type: "final" }> | null;
  error?: string | null;
  errorCode?: string | null;
  startedAt: number;
  /** The live answer's React key, kept by its saved form (the host maps the saved message's id to it): nothing remounts at the swap. */
  key: string;
  /** The saved answer's id, from the turn's `start` frame (or its `final`). */
  messageId?: string | null;
  /** The layout the `start` frame states: "inline-1" (the answer places its widgets in its text), null (the earlier layout), or
   *  undefined when no start frame stated one (see isInlineLive). */
  layout?: string | null;
  /** "Regenerate" / "Edit and resend": the question this turn replaces; it and its answer leave the transcript at once. */
  replaces?: string | null;
}

export interface SendOptions {
  attachments?: UploadedAttachment[];
  locale?: string;
  directive?: string;
  /** "✦ Dive in": what the question points at; travels as `surface.focus`. */
  focus?: SurfaceFocus | null;
  /** Shown under the user's words while the turn runs ("For my department"). */
  scope?: string | null;
  /** "Regenerate" or "Edit and resend": the id of the thread's last question, which this message replaces with its answer. */
  replaces?: string | null;
}

export interface AssistantStreamOptions {
  /** The custom page open next to the conversation, read when each message is sent. */
  page?: () => OpenPage | null | undefined | Promise<OpenPage | null | undefined>;
  /** The agent called one of the open page's client tools: hand it to the page, which posts its answer back. */
  onClientCall?: (call: ClientToolCall) => void;
  /** The ECONIC screen the person is on, read when each message is sent (builder §17.7). */
  surface?: () => SurfaceState | null | undefined;
  /** The sources of knowledge the person chose (the sources row), and under the Knowledge Bank the datasets, read when each message is sent. */
  sources?: () => KnowledgeSourceKey[] | TurnSourceChoice | null | undefined;
  /** A builder change of the turn, handed over once the turn settles (never mid-turn, builder §15.4). */
  onUi?: (event: UiEvent) => void;
  /** What the model was given about the screen (the first frame of a turn that carried a surface). */
  onSurfaceSeen?: (seen: SurfaceSeen) => void;
  /** A turn ended: `ok` when it finished without an error (a host clears an "AI unavailable" note on the next good turn). */
  onSettled?: (ok: boolean) => void;
  /** The language model did not answer (the `status: model_unavailable` frame, with its plain reason); the builder may still act. */
  onModelUnavailable?: (reason: string) => void;
}

const SURFACE_LIMIT = 16_000;

/** The surface as the server accepts it (builder §17.7: ≤ 20 widgets, ≤ 6 figures each, ≤ 16 KB); widgets are dropped from the end. */
export function fitSurface(state: SurfaceState | null | undefined, focus?: SurfaceFocus | null): SurfaceState | null {
  if (!state) return null;
  const widgets = (state.widgets ?? []).slice(0, 20).map((w) => ({ ...w, figures: w.figures?.slice(0, 6) }));
  let out: SurfaceState = { ...state, widgets, ...(focus ? { focus: { ...focus, figures: focus.figures?.slice(0, 6) } } : {}) };
  while (JSON.stringify(out).length > SURFACE_LIMIT && (out.widgets?.length ?? 0) > 0) out = { ...out, widgets: out.widgets!.slice(0, -1) };
  if (JSON.stringify(out).length > SURFACE_LIMIT && out.focus) out = { ...out, focus: { ...out.focus, figures: undefined, lineage: undefined } };
  return JSON.stringify(out).length > SURFACE_LIMIT ? null : out;
}

function parseSeen(detail: string | null | undefined): SurfaceSeen | null {
  if (!detail) return null;
  try {
    const v = JSON.parse(detail) as Partial<SurfaceSeen>;
    return { widgets: Number(v.widgets ?? 0), sent: Number(v.sent ?? 0), withheld: Array.isArray(v.withheld) ? v.withheld.map(String) : [], dropped: Array.isArray(v.dropped) ? v.dropped.map(String) : [], highest: v.highest ?? null, focusWithheld: !!v.focusWithheld };
  } catch {
    return null;
  }
}

/** Whether a thread's loaded data already holds a message (a finished turn's saved reply). */
export function holdsMessage(detail: ThreadDetail | undefined, messageId: string): boolean {
  return !!detail?.messages.some((m) => m.id === messageId);
}

/**
 * Drives one conversational turn over SSE. Accumulates events into a `live` turn for rendering, then refreshes the persisted
 * thread on `final` so the message list carries the server's version (ids, costs, citations) and the live turn is cleared.
 */
export function useAssistantStream(threadId: string | undefined, options: AssistantStreamOptions = {}) {
  const sdk = useAssistantApi();
  const qc = useQueryClient();
  const [live, setLive] = useState<LiveTurn | null>(null);
  const [streaming, setStreaming] = useState(false);
  const abortRef = useRef<AbortController | null>(null);
  const optionsRef = useRef(options);
  optionsRef.current = options;

  useEffect(() => () => abortRef.current?.abort(), []);
  // A different thread must never show the previous one's in-flight turn.
  useEffect(() => {
    abortRef.current?.abort();
    setLive(null);
    setStreaming(false);
  }, [threadId]);

  // A finished turn gives way to the saved reply only once the thread's data holds it (see `send`'s finally): watched in the query cache,
  // with one more fetch after a while should no update bring it, after which the live turn gives way regardless.
  const savedId = !streaming && live && !live.error ? live.final?.messageId ?? null : null;
  useEffect(() => {
    if (!threadId || !savedId) return;
    const key = assistantKeys.thread(threadId);
    const hash = JSON.stringify(key);
    const clear = () => setLive((l) => (l?.final?.messageId === savedId ? null : l));
    const check = () => { if (holdsMessage(qc.getQueryData<ThreadDetail>(key), savedId)) clear(); };
    check();
    const unsubscribe = qc.getQueryCache().subscribe((e) => { if (JSON.stringify(e.query.queryKey) === hash) check(); });
    const timer = window.setTimeout(() => { void qc.invalidateQueries({ queryKey: key }).finally(clear); }, 4000);
    return () => { unsubscribe(); window.clearTimeout(timer); };
  }, [threadId, savedId, qc]);

  const send = useCallback(
    async (text: string, opts: SendOptions = {}) => {
      if (!threadId || streaming) return;
      const controller = new AbortController();
      abortRef.current = controller;
      setStreaming(true);
      const surface = fitSurface(optionsRef.current.surface?.(), opts.focus);
      const sources = turnSourcesOf(optionsRef.current.sources?.());
      const startedAt = Date.now();
      const turn: LiveTurn = {
        userText: text, attachments: opts.attachments ?? [], content: "", toolCalls: [], citations: [], proposals: [], artifacts: [], confirmations: [], delegations: [], blocks: [], ui: [],
        surface: surface ? { kind: surface.kind, surface: surface.surface, route: surface.route, title: surface.title, titleAr: surface.titleAr, department: surface.department } : null,
        scope: opts.scope ?? null, startedAt, key: `turn-${startedAt}`, replaces: opts.replaces ?? null,
      };
      setLive({ ...turn });
      try {
        const request: SendMessageRequest = {
          message: text, locale: opts.locale ?? null, attachmentIds: opts.attachments?.map((a) => a.documentId) ?? null, directive: opts.directive ?? null,
          page: (await optionsRef.current.page?.()) ?? null, ...(opts.replaces ? { replaces: opts.replaces } : {}),
          ...(surface ? { surface } : {}),
          ...sources,
        };
        for await (const ev of sdk.send(threadId, request, controller.signal)) {
          if (ev.type === "status" && ev.stage === "surface") {
            turn.surfaceSeen = parseSeen(ev.detail);
            if (turn.surfaceSeen) optionsRef.current.onSurfaceSeen?.(turn.surfaceSeen);
          } else if (ev.type === "status" && ev.stage === "model_unavailable") {
            try { optionsRef.current.onModelUnavailable?.(ev.detail ?? ""); } catch { /* ignore */ }
          } else if (ev.type === "client_call") {
            optionsRef.current.onClientCall?.({ callId: ev.callId, tool: ev.tool, arguments: ev.arguments ?? {} });
          }
          applyStreamEvent(turn, ev);
          setLive({ ...turn, toolCalls: [...turn.toolCalls], citations: [...turn.citations], ui: [...turn.ui] });
        }
      } catch (e) {
        if (!controller.signal.aborted) {
          turn.error = e instanceof Error ? e.message : String(e);
          setLive({ ...turn });
        }
      } finally {
        setStreaming(false);
        abortRef.current = null;
        await qc.invalidateQueries({ queryKey: assistantKeys.thread(threadId) });
        void qc.invalidateQueries({ queryKey: ["assistant", "threads"] });
        void qc.invalidateQueries({ queryKey: ["assistant", "home"] });
        if (turn.proposals.length || turn.delegations.length) void qc.invalidateQueries({ queryKey: ["assistant", "inbox"] });
        if (turn.artifacts.length) void qc.invalidateQueries({ queryKey: ["assistant", "artifacts"] });
        // The builder's changes reach the screen once the turn has settled (builder §15.4).
        for (const ui of turn.ui) {
          try { optionsRef.current.onUi?.(ui); } catch { /* the host's handler must not break the turn */ }
        }
        if (!controller.signal.aborted) {
          // The orchestrator flags a turn the language model did not answer (`ModelUnavailable…`), even when the builder still acted.
          try { optionsRef.current.onSettled?.(!turn.error && !!turn.final && !(turn.final.guardrailFlags ?? []).some((f) => f.startsWith("ModelUnavailable"))); } catch { /* ignore */ }
        }
        // Keep a failed turn visible so the user can retry. A successful one leaves only once the thread's data holds the saved reply: an
        // invalidation superseded by a newer one (the realtime `thread.message` of the same reply) resolves before that data arrives, and
        // clearing the live turn then showed the thread as it was before the turn (a new conversation's empty hero) until the newer fetch
        // landed. The effect above clears it as soon as the reply is in the cache.
        if (!turn.error && (!turn.final?.messageId || holdsMessage(qc.getQueryData<ThreadDetail>(assistantKeys.thread(threadId)), turn.final.messageId))) setLive(null);
      }
    },
    [sdk, qc, threadId, streaming],
  );

  const abort = useCallback(() => abortRef.current?.abort(), []);
  const dismissError = useCallback(() => setLive((l) => (l?.error ? null : l)), []);

  return { live, streaming, send, abort, dismissError };
}

// ---------------------------------------------------------------- the ECONIC Agent sidebar

/** Resolves (or creates, `fresh`) the department's follow-me conversation. */
export function useSidebarThread() {
  const sdk = useAssistantApi();
  const qc = useQueryClient();
  return useSave({
    report: "silent",
    mutationFn: ({ space, locale, fresh }: { space: string; locale?: string | null; fresh?: boolean }) => sdk.sidebarThread(space, { locale, fresh }),
    onSuccess: (r) => {
      if (r.created) void qc.invalidateQueries({ queryKey: ["assistant", "threads"] });
    },
  });
}

/** "Bring this conversation" into another department. */
export function useThreadHandoff() {
  const sdk = useAssistantApi();
  const qc = useQueryClient();
  return useSave({
    report: "silent",
    mutationFn: ({ threadId, toSpace }: { threadId: string; toSpace: string }) => sdk.handoff(threadId, toSpace),
    onSuccess: (r) => {
      void qc.invalidateQueries({ queryKey: assistantKeys.thread(r.threadId) });
      void qc.invalidateQueries({ queryKey: assistantKeys.thread(r.sourceThreadId) });
      void qc.invalidateQueries({ queryKey: ["assistant", "threads"] });
    },
  });
}

/** Undo of the person's last change to a surface; refreshes the screen's layout queries. */
export function useUndoLayout() {
  const sdk = useAssistantApi();
  const qc = useQueryClient();
  return useSave({
    report: "silent",
    mutationFn: ({ surface, space }: { surface: string; space?: string | null }) => sdk.undoLayout(surface, space),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["layouts"] });
      void qc.invalidateQueries({ queryKey: ["assistant", "layouts"] });
      void qc.invalidateQueries({ queryKey: ["econic", "layout"] });
    },
  });
}

export function useAttach(threadId: string | undefined) {
  const sdk = useAssistantApi();
  return useSave({ report: "silent", mutationFn: (files: File[]) => sdk.attach(threadId!, files) });
}

// ---------------------------------------------------------------- artifacts
export function useArtifacts(filter: ArtifactFilter = {}, options?: Opts<Artifact[]>) {
  const sdk = useAssistantApi();
  return useQuery({ queryKey: assistantKeys.artifacts(filter), queryFn: () => sdk.artifacts(filter), ...options });
}

export function useArtifact(id: string | undefined, options?: Opts<Artifact>) {
  const sdk = useAssistantApi();
  return useQuery({ queryKey: assistantKeys.artifact(id ?? ""), queryFn: () => sdk.artifact(id!), enabled: !!id, ...options });
}

export function useArtifactVersions(id: string | undefined) {
  const sdk = useAssistantApi();
  return useQuery({ queryKey: assistantKeys.artifactVersions(id ?? ""), queryFn: () => sdk.artifactVersions(id!), enabled: !!id });
}

export function useEditArtifact() {
  const sdk = useAssistantApi();
  const qc = useQueryClient();
  return useSave({
    report: "silent",
    mutationFn: ({ id, edit }: { id: string; edit: EditArtifactRequest }) => sdk.editArtifact(id, edit),
    onSuccess: (a) => {
      qc.setQueryData(assistantKeys.artifact(a.id), a);
      void qc.invalidateQueries({ queryKey: ["assistant", "artifacts"] });
      void qc.invalidateQueries({ queryKey: ["assistant", "artifact"] });
      void qc.invalidateQueries({ queryKey: assistantKeys.thread(a.threadId) });
    },
  });
}

export function useExportArtifact() {
  const sdk = useAssistantApi();
  return useSave({
    report: "silent",
    mutationFn: ({ id, format, locale }: { id: string; format: ArtifactExportFormat; locale?: "en" | "ar" }) => sdk.exportArtifact(id, format, locale),
  });
}

export function useAttachArtifact() {
  const sdk = useAssistantApi();
  const qc = useQueryClient();
  return useSave({
    report: "silent",
    mutationFn: ({ id, request }: { id: string; request: AttachArtifactRequest }) => sdk.attachArtifact(id, request),
    onSuccess: (r) => {
      qc.setQueryData<Artifact>(assistantKeys.artifact(r.artifact.id), (a) => (a ? { ...a, caseId: r.artifact.caseId, caseDocumentId: r.documentId } : a));
      void qc.invalidateQueries({ queryKey: ["assistant", "artifacts"] });
      void qc.invalidateQueries({ queryKey: ["matters", "detail", r.matterId] });
    },
  });
}

// ---------------------------------------------------------------- inbox
export function useInbox(filter: InboxFilter = {}, options?: Opts<InboxPage>) {
  const sdk = useAssistantApi();
  return useQuery({ queryKey: assistantKeys.inbox(filter), queryFn: () => sdk.inbox(filter), ...options });
}

/** Proposal counts across the caller's workspaces, or one workspace's when `workspaceKey` is given. */
export function useInboxSummary(workspaceKey?: string | null, options?: Opts<InboxSummary>) {
  const sdk = useAssistantApi();
  return useQuery({ queryKey: assistantKeys.inboxSummary(workspaceKey ?? null), queryFn: () => sdk.inboxSummary(workspaceKey), staleTime: 20_000, ...options });
}

// ---------------------------------------------------------------- knowledge
export function useKnowledgeCollections(workspaceKey?: string | null, options?: Opts<KnowledgeCollection[]>) {
  const sdk = useAssistantApi();
  return useQuery({ queryKey: assistantKeys.collections(workspaceKey), queryFn: () => sdk.collections(workspaceKey), ...options });
}

export function useKnowledgeDocuments(collectionKey: string | undefined, opts: { q?: string; kind?: string; includeInactive?: boolean; take?: number } = {}, options?: Opts<KnowledgeDocument[]>) {
  const sdk = useAssistantApi();
  return useQuery({ queryKey: assistantKeys.documents(collectionKey ?? "", opts), queryFn: () => sdk.documents(collectionKey!, opts), enabled: !!collectionKey, ...options });
}

export function useKnowledgeDocument(id: string | undefined, options?: Opts<KnowledgeDocumentDetail>) {
  const sdk = useAssistantApi();
  return useQuery({ queryKey: assistantKeys.document(id ?? ""), queryFn: () => sdk.document(id!), enabled: !!id, ...options });
}

export function useUploadKnowledgeDocument(collectionKey: string | undefined) {
  const sdk = useAssistantApi();
  const qc = useQueryClient();
  return useSave({
    report: "silent",
    mutationFn: ({ file, meta }: { file: File; meta?: KnowledgeUploadMeta }) => sdk.uploadDocument(collectionKey!, file, meta),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["assistant", "knowledge"] });
      void qc.invalidateQueries({ queryKey: ["assistant", "home"] });
    },
  });
}

export function useRemoveKnowledgeDocument() {
  const sdk = useAssistantApi();
  const qc = useQueryClient();
  return useSave({
    report: "silent",
    mutationFn: (id: string) => sdk.removeDocument(id),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["assistant", "knowledge"] });
      void qc.invalidateQueries({ queryKey: ["assistant", "home"] });
    },
  });
}

export function useTools() {
  const sdk = useAssistantApi();
  return useQuery({ queryKey: assistantKeys.tools(), queryFn: () => sdk.tools(), staleTime: 300_000 });
}
